/**
 * OpenAI Realtime API Session Service
 * Handles session creation and configuration for voice agent
 */

import i18next from 'i18next'
import { logApiCall, logApiResponse, logError } from '@/lib/logger'
import { getAccessToken } from '@/services/auth-service'
import { getTqsProxyBase } from '@/lib/tqs-proxy'
import type {
  VoiceAgentConfig,
  RealtimeSessionResponse
} from '@/types/realtime'

/**
 * Get authentication headers from MSAL
 */
function getAuthHeaders(): Record<string, string> {
  const token = getAccessToken()

  if (token) {
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    }
  }

  return {
    'Content-Type': 'application/json',
  }
}

/**
 * Enhanced fetch wrapper with logging and authentication
 */
async function fetchWithLogging(url: string, options: RequestInit = {}) {
  const startTime = performance.now()
  const method = options.method || 'GET'

  // Log the API call
  logApiCall(method, url, options.body)

  // Get authentication headers
  const authHeaders = getAuthHeaders()

  try {
    const response = await fetch(url, {
      ...options,
      credentials: 'include',
      headers: {
        ...authHeaders,
        ...options.headers,
      },
    })

    const duration = performance.now() - startTime

    // Parse response
    const data = response.headers.get('content-type')?.includes('application/json')
      ? await response.json()
      : await response.text()

    // Log the response
    logApiResponse(method, url, response.status, data)

    // Log performance metrics
    if (duration > 1000) {
      logApiResponse(method, url, response.status, { duration, slow: true })
    }

    if (!response.ok) {
      throw new Error(`Realtime API Error: ${response.status} ${response.statusText}`)
    }

    return { data, status: response.status, duration }
  } catch (error) {
    const duration = performance.now() - startTime

    logError(error as Error, {
      method,
      url,
      duration,
      context: 'Realtime Session API call failed',
    })

    throw error
  }
}

/**
 * OpenAI Realtime API Session Service
 */
export const realtimeSessionService = {
  /**
   * Create a new Realtime API session via cdb_tqs backend
   * Returns ephemeral token, session ID, and client-specific voice config
   * @returns Session response with client_secret (ephemeral token), session_id, expires_at, and voice config (system_instructions, greeting_instructions, transcription_hint)
   */
  async createSession(language?: string): Promise<RealtimeSessionResponse> {
    const lang = language?.startsWith('de') ? 'de' : 'en'
    const response = await fetchWithLogging(
      `${getTqsProxyBase()}/voice-agent/session?language=${lang}`,
      {
        method: 'GET',
      }
    )

    return response.data as RealtimeSessionResponse
  },

  /**
   * Exchange the WebRTC SDP offer for the answer through the TQS relay
   * (cdb_tqs#1747). The ephemeral key from createSession travels in its own
   * header; Authorization stays the user's token for the BFF.
   */
  async exchangeSdp(relayUrl: string, ephemeralKey: string, offerSdp: string): Promise<string> {
    const token = getAccessToken()
    const response = await fetch(`${getTqsProxyBase()}/${relayUrl.replace(/^\/+/, '')}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'Content-Type': 'application/sdp',
        'X-Realtime-Client-Secret': ephemeralKey,
      },
      body: offerSdp,
    })
    if (!response.ok) {
      throw new Error(`Realtime relay failed: ${response.status} ${response.statusText}`)
    }
    return response.text()
  },

  /**
   * Get current voice agent prompts from cdb_tqs backend database
   * @returns Voice agent prompts as dictionary (system_instructions, transcription_hint, greeting_instructions)
   */
  async getConfig(): Promise<VoiceAgentConfig> {
    try {
      const response = await fetchWithLogging(
        `${getTqsProxyBase()}/prompts/system?application=voice_agent&active_only=true`,
        {
          method: 'GET',
        }
      )

      // Response is now a dictionary keyed by prompt_key
      // Map to VoiceAgentConfig structure
      const prompts = response.data as Record<string, { content: string }>

      return {
        system_instructions: prompts.system_instructions?.content || 'You are a helpful AI voice assistant.',
        transcription_hint: prompts.transcription_hint?.content || '',
        greeting_instructions: prompts.greeting_instructions?.content || i18next.t('voiceAgent.greetingFallbackCDB'),
        voice: prompts.voice?.content || 'alloy',
        model: prompts.model?.content || 'gpt-4o-realtime-preview-2024-12-17',
        turn_detection_type: prompts.turn_detection_type?.content || 'server_vad',
        turn_detection_eagerness: prompts.turn_detection_eagerness?.content || 'balanced',
        turn_detection_create_response: prompts.turn_detection_create_response?.content
          ? prompts.turn_detection_create_response.content === 'true'
          : true,
        turn_detection_interrupt_response: prompts.turn_detection_interrupt_response?.content
          ? prompts.turn_detection_interrupt_response.content === 'true'
          : false,
      } as VoiceAgentConfig
    } catch (error) {
      // If prompts endpoint fails (404 or other error), return default config
      logError(error as Error, {
        context: 'Failed to fetch voice agent config, using defaults',
      })

      return {
        system_instructions: 'You are a helpful AI voice assistant. Be concise and friendly.',
        transcription_hint: '',
        greeting_instructions: i18next.t('voiceAgent.greetingFallback'),
        voice: 'alloy',
        model: 'gpt-4o-realtime-preview-2024-12-17',
        turn_detection_type: 'server_vad',
        turn_detection_eagerness: 'balanced',
        turn_detection_create_response: true,
        turn_detection_interrupt_response: false,
      } as VoiceAgentConfig
    }
  },

  /**
   * Update voice agent prompt in cdb_tqs backend database
   * @param promptKey - The prompt key to update (system_instructions, transcription_hint, greeting_instructions)
   * @param content - The new prompt content
   * @returns Updated prompt
   */
  async updatePrompt(promptKey: string, content: string): Promise<any> {
    const response = await fetchWithLogging(
      `${getTqsProxyBase()}/prompts/system/${promptKey}?application=voice_agent`,
      {
        method: 'PUT',
        body: JSON.stringify({
          content,
          is_active: true,
        }),
      }
    )

    return response.data
  },
}
